import { isBefore } from 'date-fns';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { getChannelDefaults } from '@/components/pages/job-posting/channel-defaults';
import { FormDatePicker } from '@/components/ui/date-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { JOVEO, TALROO } from '@/types/channels';
import { addDays, durationInDays, formatShortDate } from '@/utils/date-util';

export function Dates() {
  const intl = useIntl();
  const { channelSlug } = useParams();
  const { campaign, hookForm, isFetching, isCompleted, isScheduled } =
    useEditCampaignContext();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = hookForm;

  const hiringGoal = campaign?.hiring_goal;
  const hiringGoalStartAt = hiringGoal?.start_date;
  const hiringGoalEndAt = hiringGoal?.hire_by;
  const hiringGoalDuration = hiringGoal?.duration;
  const isReadonly = campaign?.is_readonly;

  const startsAt = watch('starts_at');
  const endAt = watch('end_at');
  const duration = endAt && startsAt ? durationInDays(startsAt, endAt) : null;

  // Start date is enabled only for non readonly scheduled campaigns. Disabled for readonly campaigns and non scheduled campaigns.
  const isStartDateDisabled = !isScheduled || isReadonly;

  // End date is disabled for reach, Joveo non scheduled campaigns, completed campaigns and readonly campaigns.
  const isEndDateDisabled =
    (channelSlug === JOVEO && !isScheduled) ||
    (channelSlug === TALROO && !isScheduled) ||
    isCompleted ||
    isReadonly;

  const channelDefaults = channelSlug
    ? getChannelDefaults(channelSlug)
    : undefined;
  const minDuration =
    channelDefaults?.minDuration ?? channelDefaults?.duration ?? 1;

  // Calculate minimum end date based on start date and channel constraints. The dates before this will be disabled in the date picker.
  const minEndDate = useMemo(() => {
    if (!startsAt || !minDuration) return undefined;

    const startDate = new Date(startsAt);
    const endDate = addDays(startDate, minDuration);

    if (isBefore(endDate, new Date())) return new Date();

    return endDate;
  }, [startsAt, minDuration]);

  // Handle start date change and auto-update end date based on constraints
  const handleStartDateChange = (newStartDate: string) => {
    const startDate = new Date(newStartDate);
    const calculatedEndDate = addDays(startDate, minDuration).toISOString();

    if (isBefore(calculatedEndDate, new Date())) return;

    // Case 1: Fixed duration - always set the end date
    if (channelDefaults?.duration) {
      setValue('end_at', calculatedEndDate);
      return;
    }

    // Case 2: Minimum duration - only set if not set or if it violates minimum
    if (channelDefaults?.minDuration || minDuration) {
      const currentEndDate = endAt ? new Date(endAt) : null;
      if (!currentEndDate || currentEndDate < new Date(calculatedEndDate)) {
        setValue('end_at', calculatedEndDate);
      }
    }
  };

  return (
    <div className="flex w-full gap-4">
      <div className="flex w-full flex-col gap-1">
        <FormDatePicker
          control={control}
          name="starts_at"
          label={intl.formatMessage({
            defaultMessage: 'Start date',
            description: 'Form label: first day the edited campaign is active',
          })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter start date',
            description:
              'Placeholder for campaign or flight start in date picker',
          })}
          isDisabled={isStartDateDisabled}
          isStartOfDay
          isFetching={isFetching}
          onChange={handleStartDateChange}
        />
        {!hiringGoalStartAt && isFetching && (
          <Skeleton className="my-0.5 h-3 w-50" />
        )}
        {hiringGoalStartAt && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Active hiring goal started {startsAt}"
              values={{ startsAt: formatShortDate(hiringGoalStartAt) }}
              description="Context line: hire-by window start; {startsAt} is a formatted date"
            />
          </Typography>
        )}
      </div>

      <div className="flex w-full flex-col gap-1">
        <FormDatePicker
          control={control}
          name="end_at"
          label={intl.formatMessage({
            defaultMessage: 'End date',
            description:
              'Form label: last day the campaign can run (or N/A for ongoing)',
          })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter end date',
            description: 'Placeholder for campaign end in date picker',
          })}
          isEndOfDay
          isFetching={isFetching}
          isDisabled={isEndDateDisabled}
          minDate={minEndDate}
          error={errors.end_at?.message}
        />
        {!hiringGoalEndAt && isFetching && (
          <Skeleton className="my-0.5 h-3 w-50" />
        )}
        {hiringGoalEndAt ? (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Active hiring goal ends {endAt}"
              values={{ endAt: formatShortDate(hiringGoalEndAt) }}
              description="Context line: hire-by deadline; {endAt} is a formatted date"
            />
          </Typography>
        ) : (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="No hiring goal end date set"
              description="Message shown when no end date is set for the hiring goal"
            />
          </Typography>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Typography variant="subtitle2">
          <FormattedMessage
            defaultMessage="Duration"
            description="Read-only: length of the campaign in calendar days"
          />
        </Typography>

        <Typography
          variant="body1"
          className="flex h-8 items-center justify-start whitespace-nowrap"
        >
          {!duration && isFetching && <Skeleton className="h-4 w-12" />}
          {duration && (
            <FormattedMessage
              defaultMessage="{duration} days"
              values={{ duration }}
              description="{duration} is the numeric day count between start and end"
            />
          )}
        </Typography>

        <Typography
          variant="small1"
          className="!text-fx-neutral-600 flex flex-1 items-center whitespace-nowrap"
        >
          {!hiringGoalDuration && isFetching && (
            <Skeleton className="h-3 w-10" />
          )}
          {hiringGoalDuration && (
            <FormattedMessage
              defaultMessage="{duration} days"
              values={{ duration: hiringGoalDuration }}
              description="Hire goal context: {duration} is days in the current hire-by window"
            />
          )}
        </Typography>
      </div>
    </div>
  );
}
